import { motion } from "framer-motion";
import { Sparkles, BookMarked, LifeBuoy, Coins } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { PhoneMockup } from "@/components/PhoneMockup";
import { SCREENS } from "@/data/content";

const ICONS = [BookMarked, LifeBuoy, Coins];

export const Skadi = () => {
  const { t } = useLanguage();
  const s = t.skadi;

  return (
    <section id="skadi" className="relative scroll-mt-24 overflow-hidden py-24 md:py-32">
      <div className="absolute inset-0" aria-hidden="true">
        <div className="breathe absolute left-1/2 top-1/2 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(230,184,74,0.16),transparent_60%)]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-6 md:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="order-2 flex justify-center lg:order-1 lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="relative w-[260px] sm:w-[300px]"
          >
            <div className="absolute -inset-12 rounded-full bg-[var(--amber)]/10 blur-3xl" aria-hidden="true" />
            <PhoneMockup src={SCREENS.skadi} alt={s.screenLabel} label={s.screenLabel} testId="skadi-phone" />
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="absolute -right-6 top-[18%] flex h-14 w-14 items-center justify-center rounded-full bg-[var(--amber)] shadow-[0_20px_40px_-10px_rgba(230,184,74,0.6)] sm:-right-10"
              aria-hidden="true"
            >
              <Sparkles className="h-6 w-6 text-[#1a1408]" />
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="order-1 lg:order-2 lg:col-span-7"
        >
          <SectionHeading index={s.index} eyebrow={s.eyebrow} title={s.title} body={s.subtitle} />

          <div className="mt-9 space-y-3">
            {s.examples.map((q, i) => (
              <motion.div
                key={q}
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 + i * 0.12 }}
                className="inline-flex max-w-full items-start gap-3 rounded-2xl rounded-tl-md border border-[var(--line)] bg-[var(--surface)] px-4 py-3"
              >
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--amber)]" />
                <span className="text-sm text-[var(--ink)] sm:text-base">„{q}“</span>
              </motion.div>
            ))}
          </div>

          <dl className="mt-10 grid gap-6 sm:grid-cols-3">
            {s.points.map((p, i) => {
              const Icon = ICONS[i] || Sparkles;
              return (
                <div key={p.title}>
                  <Icon className="h-5 w-5 text-[var(--amber)]" />
                  <dt className="mt-3 font-semibold text-[var(--ink)]">{p.title}</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-[var(--ink-2)]">{p.desc}</dd>
                </div>
              );
            })}
          </dl>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-widest text-[var(--ink-3)]">{s.note}</p>
        </motion.div>
      </div>
    </section>
  );
};
